import { useEffect, useState } from 'react';
import { api, tokenStore, type ChangeRequest } from '../api-client';

export default function Requests() {
  const [pastRequests, setPastRequests] = useState<ChangeRequest[]>([]);
  const [requestType, setRequestType] = useState<'new_scenario' | 'modify' | 'pause'>(
    'new_scenario',
  );
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenStore.get()) {
      window.location.href = '/login';
      return;
    }
    loadRequests();
  }, []);

  async function loadRequests() {
    try {
      const r = await api.listChangeRequests();
      setPastRequests(r.requests);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.createChangeRequest({ requestType, title, description });
      setSent(true);
      await loadRequests();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">設定変更依頼</h2>
      <p className="text-sm text-slate-500 mb-6">
        新シナリオ追加、設定変更、一時停止などのご依頼はこちらから
      </p>

      <div className="bg-white rounded-lg shadow border p-5 mb-6">
        <h3 className="font-semibold mb-3">新しい依頼</h3>

        {sent ? (
          <div className="bg-green-50 p-4 rounded border border-green-200">
            <p className="text-green-800">✓ 依頼を受け付けました</p>
            <p className="text-xs text-slate-600 mt-2">
              Craval担当者が3営業日以内にご連絡いたします
            </p>
            <button
              onClick={() => {
                setSent(false);
                setTitle('');
                setDescription('');
              }}
              className="mt-3 text-sm text-blue-600 hover:underline"
            >
              新しい依頼を作成
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">依頼種別</label>
              <select
                value={requestType}
                onChange={(e) => setRequestType(e.target.value as never)}
                className="w-full px-3 py-2 border rounded"
              >
                <option value="new_scenario">新シナリオ追加</option>
                <option value="modify">既存シナリオの設定変更</option>
                <option value="pause">一時停止・再開</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">タイトル</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="例: freee 連携を追加したい"
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">詳細</label>
              <textarea
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                placeholder="ご要望の詳細をご記入ください"
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="submit"
              className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700"
            >
              依頼を送信
            </button>
          </form>
        )}
      </div>

      <div className="bg-white rounded-lg shadow border">
        <div className="p-5 border-b">
          <h3 className="font-semibold">過去の依頼履歴</h3>
        </div>
        {pastRequests.length === 0 ? (
          <p className="p-5 text-slate-500 text-sm">まだ依頼がありません。</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-4 py-2">タイトル</th>
                <th className="px-4 py-2">種別</th>
                <th className="px-4 py-2">状態</th>
                <th className="px-4 py-2">日付</th>
              </tr>
            </thead>
            <tbody>
              {pastRequests.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-3">{r.title}</td>
                  <td className="px-4 py-3 text-slate-600">{translateType(r.request_type)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {new Date(r.created_at).toLocaleDateString('ja-JP')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function translateType(type: string): string {
  const map: Record<string, string> = {
    new_scenario: '新規',
    modify: '変更',
    pause: '停止',
    delete: '削除',
    question: '質問',
  };
  return map[type] ?? type;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; className: string }> = {
    pending: { label: '受付', className: 'bg-amber-100 text-amber-800' },
    in_progress: { label: '対応中', className: 'bg-blue-100 text-blue-800' },
    completed: { label: '対応済み', className: 'bg-green-100 text-green-800' },
    rejected: { label: '却下', className: 'bg-slate-200 text-slate-700' },
  };
  const v = map[status] ?? { label: status, className: 'bg-slate-100' };
  return <span className={`px-2 py-1 text-xs rounded ${v.className}`}>{v.label}</span>;
}
